import React from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { TotemButton } from 'ui/Button';
import { InlineError } from 'ui/InlineError';

import { CompanySearchSelect, SelectedCompany } from 'components/CompanySearchSelect';

import { TotemInput, TotemLabel } from 'styles';

import { validateEmail } from 'utils';
import { isValidVatFormat } from 'utils/vat';
import { track, analyticsEvents, analyticsPropertiesConstants, analyticsSourceConstants } from 'tracking/segment';
import { ClipLoader } from 'react-spinners';
import {
    BillingInfoFragmentFragment,
    GetOrganizationQuery,
    GetUserQuery,
    OrganizationBillingInfoInput,
    TotemFragmentFragment as Totem,
    UpdateOrganizationBillingInfoMutationVariables,
    useUpdateOrganizationBillingInfoMutation,
} from 'data/__generated__';

interface BillingInfoFormProps {
    currentTotem: Totem | undefined;
    currentUser: GetUserQuery['user'] | null;
    organization: GetOrganizationQuery['organizationOfUser'] | null;
    // Fournies par le parent, qui n'affiche ce formulaire qu'une fois la requête dédiée résolue : les
    // `defaultValues` de react-hook-form sont figées au montage, elles ne doivent pas partir de `null`.
    billingInfo: BillingInfoFragmentFragment | null;
    setIsModifyingBillingInfo: (isModifying: boolean) => void;
}

export const BillingInfoForm: React.FC<BillingInfoFormProps> = ({
    currentTotem,
    currentUser,
    organization,
    billingInfo,
    setIsModifyingBillingInfo,
}) => {
    const [updateOrganizationBillingInfo] = useUpdateOrganizationBillingInfoMutation();
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm({
        defaultValues: {
            invoiceEmail: billingInfo?.invoiceEmail || currentUser?.emails?.[0].address,
            invoiceName: billingInfo?.invoiceName || organization?.name,
            invoiceAddress: billingInfo?.invoiceAddress || currentTotem?.address,
            invoiceCity: billingInfo?.invoiceCity || currentTotem?.city,
            invoiceZipcode: billingInfo?.invoiceZipcode || currentTotem?.zipcode,
            invoiceCountry: billingInfo?.invoiceCountry || currentTotem?.country,
            invoiceVAT: billingInfo?.invoiceVAT,
            invoiceSiret: billingInfo?.invoiceSiret,
            invoicePO: billingInfo?.invoicePO,
        },
    });
    // Sélection Sirene : identité (raison sociale, TVA, SIRET) fait autorité ; l'adresse ne remplace que
    // si Sirene l'a fournie, pour ne pas écraser une saisie existante par du vide.
    function handleCompanySelect(company: SelectedCompany) {
        setValue('invoiceName', company.legalName, { shouldValidate: true, shouldDirty: true });
        setValue('invoiceSiret', company.siret, { shouldDirty: true });
        if (company.vat) {
            setValue('invoiceVAT', company.vat, { shouldValidate: true, shouldDirty: true });
        }
        if (company.address) {
            setValue('invoiceAddress', company.address, { shouldValidate: true, shouldDirty: true });
        }
        if (company.zipcode) {
            setValue('invoiceZipcode', company.zipcode, { shouldValidate: true, shouldDirty: true });
        }
        if (company.city) {
            setValue('invoiceCity', company.city, { shouldValidate: true, shouldDirty: true });
        }
    }
    async function handleBillingInfoSubmit(organizationBillingInfo: BillingInfoFragmentFragment) {
        if (!organization?._id) {
            return;
        }
        const options: { variables: UpdateOrganizationBillingInfoMutationVariables } = {
            variables: {
                organizationId: organization!._id,
                organizationBillingInfo: organizationBillingInfo as OrganizationBillingInfoInput,
            },
        };
        await updateOrganizationBillingInfo(options);
        track(analyticsEvents.VALIDATE_BILLING_INFO, {
            [analyticsPropertiesConstants.COMMON.IS_ONBOARDING]: true,
            [analyticsPropertiesConstants.COMMON.SOURCE]: analyticsSourceConstants.CHECKOUT_BASKET,
            [analyticsPropertiesConstants.ORDER.BILLING.HAS_PO_NUMBER]: !!organizationBillingInfo?.invoicePO,
            [analyticsPropertiesConstants.ORDER.BILLING.HAS_TVA_NUMBER]: !!organizationBillingInfo?.invoiceVAT,
        });
        setIsModifyingBillingInfo(false);
    }
    return (
        <BillingInfoFormContainer onSubmit={handleSubmit(handleBillingInfoSubmit)}>
            <TotemLabel>
                Identifier mon entreprise
                <CompanySearchSelect onSelect={handleCompanySelect} />
                <FieldHint>
                    Recherchez votre société par nom, SIRET ou n° de TVA pour pré-remplir la raison sociale, le n° de
                    TVA, le SIRET et l'adresse.
                </FieldHint>
            </TotemLabel>
            <TotemLabel>
                Email de réception des factures* <InlineError display={errors.invoiceEmail} />
                <TotemInput
                    placeholder="Entrez l'email de réception des factures"
                    type="email"
                    {...register('invoiceEmail', {
                        required: true,
                        validate: validateEmail('Votre mail n’est pas valide'),
                        setValueAs: (email) => email.trim(),
                    })}
                    data-test="payment-email"
                />
            </TotemLabel>
            <TotemLabel>
                Raison sociale <InlineError display={errors.invoiceName} />
                <TotemInput
                    placeholder="Entrez le nom d'entreprise pour la facture"
                    {...register('invoiceName', {
                        required: true,
                        minLength: {
                            value: 3,
                            message: "Le nom de l'entreprise doit contenir au moins 3 caractères",
                        },
                    })}
                    data-test="payment-name"
                />
            </TotemLabel>
            <Line>
                <TotemLabel $halfWidth>
                    Numéro de PO <InlineError display={errors.invoicePO} />
                    <TotemInput
                        placeholder="Entrez le n° de PO"
                        {...register('invoicePO')}
                        data-test="payment-PO-number"
                    />
                </TotemLabel>
                <TotemLabel $halfWidth>
                    Numéro de TVA <InlineError display={errors.invoiceVAT} />
                    <TotemInput
                        placeholder="Entrez le n° de TVA"
                        {...register('invoiceVAT', {
                            validate: (value) => isValidVatFormat(value) || 'Le format du numéro de TVA est invalide',
                        })}
                        data-test="payment-VAT-number"
                    />
                </TotemLabel>
            </Line>
            <TotemLabel>
                SIRET <InlineError display={errors.invoiceSiret} />
                <TotemInput placeholder="Entrez le SIRET" {...register('invoiceSiret')} data-test="payment-siret" />
            </TotemLabel>
            <TotemLabel>
                Adresse de facturation* <InlineError display={errors.invoiceAddress} />
                <TotemInput
                    placeholder="Adresse"
                    {...register('invoiceAddress', { required: true })}
                    data-test="payment-address"
                />
            </TotemLabel>
            <Line>
                <TotemLabel $halfWidth>
                    Ville* <InlineError display={errors.invoiceCity} />
                    <TotemInput
                        placeholder="Entrez la ville"
                        {...register('invoiceCity', { required: true })}
                        data-test="payment-city"
                    />
                </TotemLabel>
                <TotemLabel $halfWidth>
                    Code postal* <InlineError display={errors.invoiceZipcode} />
                    <TotemInput
                        placeholder="Entrez le code postal"
                        {...register('invoiceZipcode', { required: true })}
                        data-test="payment-zipcode"
                    />
                </TotemLabel>
            </Line>
            <TotemLabel>
                Pays* <InlineError display={errors.invoiceCountry} />
                <TotemInput
                    placeholder="Entrez le pays"
                    {...register('invoiceCountry', { required: true })}
                    data-test="payment-country"
                />
            </TotemLabel>
            <div>Rentrez les informations telles que vous souhaitez qu'elles apparaissent sur la facture.</div>
            <TotemButton
                disabled={isSubmitting}
                $margins={[1, 0, 0, 0]}
                $fontSize="1em"
                type="submit"
                data-test="validate-billing-info"
            >
                {isSubmitting ? <ClipLoader color="white" size="16px" /> : 'Valider les infos de facturation'}
            </TotemButton>
        </BillingInfoFormContainer>
    );
};

const BillingInfoFormContainer = styled.form`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 0;
`;

const Line = styled.div`
    display: flex;
    justify-content: space-between;
`;

const FieldHint = styled.span`
    margin-top: 4px;
    font-size: 0.85em;
    font-weight: normal;
    color: ${(props) => props.theme.colors.pantinGrey};
`;
